/**
 * Search Bar Component
 *
 * Search input with an integrated combobox dropdown that merges:
 *   - Recent searches (from `useSearchHistory`, prefix-filtered when typing)
 *   - Server-side type-ahead title suggestions (from `useSuggestions` →
 *     `/api/v1/suggest` keyword endpoint)
 *
 * Implements the W3C ARIA 1.2 combobox pattern: DOM focus stays on the input
 * and the active option is tracked via `aria-activedescendant`.
 */

import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
  type KeyboardEvent,
  type ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import {
  SearchSuggestionsDropdown,
} from './SearchSuggestionsDropdown';
import { AllFiltersDialog } from './AllFiltersDialog';
import { useSearchHistory } from '../../hooks/useSearchHistory';
import { useSuggestions } from '../../hooks/useSuggestions';
import { useFacets } from '../../hooks/useFacets';
import { getSourceFallbackIcon } from '../../utils/sourceIcons';
import { trackEvent } from '../../services/googleAnalytics';
import {
  HISTORY_MAX_DESKTOP,
  HISTORY_MAX_MOBILE,
  MIN_SUGGEST_LENGTH,
  SUGGESTION_MAX,
  mergeSuggestionRows,
  type DropdownRow,
} from '../../searchCore/rows';
import type { SearchFilters } from '../../types/search';

const LISTBOX_ID = 'search-suggestions-listbox';
const OPTION_ID_PREFIX = 'search-suggestions-option';

interface SearchBarProps {
  /** Current search query */
  query: string;
  /** Callback when query changes */
  onQueryChange: (query: string) => void;
  /** Callback when search is submitted */
  onSearch: (query: string) => void;
  /** Whether a search is in progress */
  isLoading?: boolean;
  /** Whether to show the "All filters" button (landing page only) */
  showAllFiltersButton?: boolean;
  /** Current filters (required when showAllFiltersButton is true) */
  filters?: SearchFilters;
  /** Callback when filters change (required when showAllFiltersButton is true) */
  onFiltersChange?: (filters: SearchFilters) => void;
  /** Optional user language for the suggestions filter (passed through to /suggest). */
  userLanguage?: string;
}

export function SearchBar({
  query,
  onQueryChange,
  onSearch,
  isLoading = false,
  showAllFiltersButton = false,
  filters = {},
  onFiltersChange,
  userLanguage,
}: SearchBarProps) {
  const { t } = useTranslation('search');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [filtersDialogOpen, setFiltersDialogOpen] = useState(false);
  const composingRef = useRef(false);

  const { history, addEntry, removeEntry, clearHistory } = useSearchHistory();
  const { getDisplayName } = useFacets();
  const containerRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Suspend fetches during IME composition: pass an empty string while composing
  // so the hook doesn't fire requests with intermediate Hiragana/Pinyin state.
  const fetchQuery = composingRef.current ? '' : query;

  const { suggestions, isLoading: suggestionsLoading } = useSuggestions(fetchQuery, {
    minLength: MIN_SUGGEST_LENGTH,
    debounceMs: 120,
    size: SUGGESTION_MAX,
    userLanguage,
    audience: 'search_frontend',
  });

  /**
   * Build the unified row list shown in the dropdown.
   * - Empty / sub-min query: show recent history only.
   * - ≥ minLength chars: prefix-filter history (cap 3) + server suggestions (cap 5).
   *   Suggestions whose text matches an already-shown history entry are dropped.
   */
  const rows = useMemo<DropdownRow[]>(
    () =>
      mergeSuggestionRows({
        query,
        history,
        suggestions,
        historyMax: isMobile ? HISTORY_MAX_MOBILE : HISTORY_MAX_DESKTOP,
        filteredHistoryMax: HISTORY_MAX_MOBILE,
        minLength: MIN_SUGGEST_LENGTH,
      }),
    [query, history, suggestions, isMobile]
  );

  const showDropdown = dropdownOpen && rows.length > 0;
  const hasAnyHistory = rows.some((r) => r.kind === 'history');

  const activeFilterCount = useMemo(
    () =>
      Object.values(filters).reduce<number>(
        (count, v) => count + (Array.isArray(v) ? v.length : v ? 1 : 0),
        0
      ),
    [filters]
  );

  // Reset active index whenever the row set changes.
  useEffect(() => {
    setActiveIndex(-1);
  }, [rows]);

  // Close on outside click.
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const executeSearch = useCallback(
    (searchQuery: string) => {
      const trimmed = searchQuery.trim();
      if (!trimmed) return;
      addEntry(trimmed);
      setDropdownOpen(false);
      setActiveIndex(-1);
      onQueryChange(trimmed);
      onSearch(trimmed);
    },
    [addEntry, onSearch, onQueryChange]
  );

  const handleSelectRow = useCallback(
    (row: DropdownRow) => {
      const value = row.kind === 'history' ? row.value : row.value.text;
      trackEvent('search_suggestion_selected', {
        kind: row.kind,
        position: rows.indexOf(row) + 1,
        typed_query: query.trim(),
        search_term: value,
      });
      executeSearch(value);
    },
    [executeSearch, rows, query]
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onQueryChange(event.target.value);
      setDropdownOpen(true);
    },
    [onQueryChange]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (composingRef.current) return; // ignore keys mid-composition

      if (showDropdown) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setActiveIndex((prev) => (prev < rows.length - 1 ? prev + 1 : 0));
            return;
          case 'ArrowUp':
            event.preventDefault();
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : rows.length - 1));
            return;
          case 'Enter':
            if (activeIndex >= 0 && activeIndex < rows.length) {
              event.preventDefault();
              handleSelectRow(rows[activeIndex]);
              return;
            }
            break;
          case 'Tab':
            // Inline-complete: if a row is highlighted, copy its text into
            // the input without submitting. Lets the user refine before search.
            if (activeIndex >= 0) {
              const row = rows[activeIndex];
              const text = row.kind === 'history' ? row.value : row.value.text;
              event.preventDefault();
              onQueryChange(text);
              setActiveIndex(-1);
              return;
            }
            break;
          case 'Escape':
            event.preventDefault();
            setDropdownOpen(false);
            setActiveIndex(-1);
            return;
        }
      }

      if (event.key === 'Enter' && query.trim()) {
        executeSearch(query);
      }
    },
    [showDropdown, rows, activeIndex, query, executeSearch, handleSelectRow, onQueryChange]
  );

  const handleFocus = useCallback(() => {
    setDropdownOpen(true);
  }, []);

  const handleHistoryRemove = useCallback(
    (removedQuery: string) => {
      removeEntry(removedQuery);
    },
    [removeEntry]
  );

  const handleHistoryClear = useCallback(() => {
    clearHistory();
    setDropdownOpen(false);
    setActiveIndex(-1);
  }, [clearHistory]);

  const handleClearQuery = useCallback(() => {
    onQueryChange('');
  }, [onQueryChange]);

  const handleCompositionStart = useCallback(() => {
    composingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(() => {
    composingRef.current = false;
  }, []);

  const activeDescendantId =
    activeIndex >= 0 ? `${OPTION_ID_PREFIX}-${activeIndex}` : undefined;

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      <Paper
        elevation={0}
        sx={{
          border: 'none',
          // Deliberate outlier: the search bar stays rounder than the sharpened
          // COPA-DATA corner scale, so use explicit px instead of theme units.
          borderRadius: showDropdown ? '8px 8px 0 0' : '32px',
          overflow: 'hidden',
          bgcolor: 'surfaceContainer',
        }}
      >
        {/* Main Search Input Row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            px: 2,
            py: 1,
          }}
        >
          <SearchIcon sx={{ color: 'text.secondary', mr: 1.5 }} />

          <InputBase
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            placeholder={t('placeholder')}
            disabled={isLoading}
            sx={{
              flex: 1,
              fontSize: '1.1rem',
              '& input': {
                py: 1,
              },
            }}
            inputProps={{
              'aria-label': 'search',
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': showDropdown,
              'aria-controls': LISTBOX_ID,
              'aria-activedescendant': activeDescendantId,
              autoComplete: 'off',
              autoCorrect: 'off',
              autoCapitalize: 'off',
              spellCheck: false,
            }}
          />

          {/* Clear button */}
          {query && (
            <IconButton
              size="small"
              onClick={handleClearQuery}
              sx={{ mr: 1 }}
              aria-label="clear search"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}

          {/* All filters button - shown only on landing page */}
          {showAllFiltersButton && (
            <Button
              variant="text"
              size="small"
              startIcon={<TuneIcon />}
              onClick={() => setFiltersDialogOpen(true)}
              disableRipple
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                '&:hover': {
                  bgcolor: 'transparent',
                  color: 'primary.main',
                },
              }}
            >
              {t('filters.title')}
              {activeFilterCount > 0 && ` (${activeFilterCount})`}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Dropdown — absolutely positioned below the search bar */}
      {showDropdown && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.modal,
            bgcolor: 'surfaceContainer',
            borderRadius: '0 0 8px 8px',
            boxShadow: 3,
            overflow: 'hidden',
          }}
        >
          <SearchSuggestionsDropdown
            rows={rows}
            activeIndex={activeIndex}
            query={query}
            isLoading={suggestionsLoading}
            onSelect={handleSelectRow}
            onRemoveHistory={handleHistoryRemove}
            onClearHistory={handleHistoryClear}
            listboxId={LISTBOX_ID}
            optionIdPrefix={OPTION_ID_PREFIX}
            hasAnyHistory={hasAnyHistory}
            resolveSourceIcon={getSourceFallbackIcon}
            resolveSourceTypeLabel={(value) =>
              value
                ? t(`facets.values.source_type.${value}`, {
                    defaultValue: getDisplayName('source_type', value),
                  })
                : undefined
            }
          />
        </Box>
      )}

      {/* All Filters Dialog */}
      {showAllFiltersButton && onFiltersChange && (
        <AllFiltersDialog
          open={filtersDialogOpen}
          onClose={() => setFiltersDialogOpen(false)}
          filters={filters}
          onChange={onFiltersChange}
        />
      )}
    </Box>
  );
}
